/**
 * Reporting relayed consent decisions for audit.
 *
 * A decision relayed from another domain is the basis on which measurement runs
 * — or does not — so what was relayed and what became of it has to be
 * recoverable after the fact. The browser is the only place that knows, so the
 * outcome is reported to our own server, which writes it to the structured log.
 *
 * What is reported is deliberately thin: the relay's shape, the safeguard
 * outcome, the token version, how old the decision was and the four consent
 * mode signals. The raw token never leaves the browser, and nothing here
 * identifies the guest, the booking or the venue.
 */
import { call as rpc } from "../../common/jsonRpcRoutes";
import type { HostConsentRelay } from "./hostConsent";

/** Relays already reported, by identity, so a re-render does not report twice. */
const reported = new WeakSet<HostConsentRelay>();

/**
 * Reports a relayed decision, once per relay.
 *
 * Auditing must never affect the journey: a rejected or unavailable request is
 * swallowed, and the call is not awaited.
 */
export function reportConsentRelay(relay: HostConsentRelay): void {
  if (relay.outcome === "none" || relay.shape === "none") return;
  if (reported.has(relay)) return;
  reported.add(relay);

  void rpc("/analytics/consent-relay-audit", {
    shape: relay.shape,
    outcome: relay.outcome,
    version: relay.version ?? undefined,
    ageSeconds: relay.ageSeconds ?? undefined,
    signals: relay.signals ?? undefined,
  }).catch(() => {
    // Audit is best effort: an offline guest or a blocked request must not
    // change what the checkout does with the decision.
  });
}
